import i18next from 'i18next';
import { projectStatusApi } from './api';

// The status labels ship bilingually (label_sv/label_en). Pick by the active
// UI language so status text follows the user's language choice everywhere the
// shared helpers are used (lists, timelines, home widgets).
const isEn = (): boolean => (i18next.language || 'sv').toLowerCase().startsWith('en');

export interface StatusSubStep {
  key: string;
  label_sv: string;
  label_en: string;
  dotColor: string;
  sortOrder: number;
}

export interface StatusStep {
  sortOrder: number;
  key: string;
  label_sv: string;
  label_en: string;
  badgeColor: string;
  isSpecial: boolean;
  subSteps?: StatusSubStep[];
  hideInTimeline?: boolean;
}

export interface ProjectStatusInstance {
  projectId: string;
  currentStepSortOrder: number;
  currentSubStatus: string | null;
  steps: StatusStep[];
}

export interface StatusOption {
  value: string;
  label: string;
}

// Fallback options when no dynamic template is loaded (bilingual).
const FALLBACK_STATUS: Array<{ value: string; label_sv: string; label_en: string }> = [
  { value: 'not_started', label_sv: 'Ej Startat', label_en: 'Not started' },
  { value: 'contract', label_sv: 'Kontrakt', label_en: 'Contract' },
  { value: 'payment', label_sv: 'Insättning', label_en: 'Deposit' },
  { value: 'in_progress', label_sv: 'Pågående', label_en: 'In progress' },
  { value: 'milestone', label_sv: 'Etappmål', label_en: 'Milestone' },
  { value: 'ready_for_attestation', label_sv: 'Klar för attestering', label_en: 'Ready for attestation' },
  { value: 'ready_for_reattestation', label_sv: 'Klar för ny attestering', label_en: 'Ready for re-attestation' },
  { value: 'seller_attested', label_sv: 'Granskas', label_en: 'Under review' },
  { value: 'jointly_attested', label_sv: 'Godkänd', label_en: 'Approved' },
  { value: 'payout_processing', label_sv: 'Utbetalning pågår', label_en: 'Payout in progress' },
  { value: 'payout_failed', label_sv: 'Utbetalningsfel', label_en: 'Payout failed' },
  { value: 'paid', label_sv: 'Utbetald', label_en: 'Paid' },
  { value: 'under_resolution', label_sv: 'Åtgärdas', label_en: 'Being remedied' },
  { value: 'disputed', label_sv: 'Under invändning', label_en: 'Under objection' },
  { value: 'external_dispute', label_sv: 'Tvist', label_en: 'Dispute' },
  { value: 'on_hold', label_sv: 'Vilande', label_en: 'On hold' },
  { value: 'conflict', label_sv: 'Konflikt', label_en: 'Conflict' },
  { value: 'dispute', label_sv: 'Tvist', label_en: 'Dispute' },
  { value: 'completed', label_sv: 'Avslutat', label_en: 'Completed' }
];

const fallbackLabel = (value: string): string => {
  const found = FALLBACK_STATUS.find((o) => o.value === value);
  return found ? (isEn() ? found.label_en : found.label_sv) : value;
};

// Kept for backward compatibility; resolves to the active language on read.
export const fallbackStatusOptions: StatusOption[] = FALLBACK_STATUS.map((s) => ({
  value: s.value,
  label: isEn() ? s.label_en : s.label_sv
}));

export function canonicalProjectStatusKey(statusKey: string): string {
  return ['contract_draft', 'contract_signing'].includes(statusKey)
    ? 'contract'
    : statusKey;
}

function canonicalProjectOverviewStatusKey(statusKey: string): string {
  const canonicalStatusKey = canonicalProjectStatusKey(statusKey);
  if (
    [
      'ready_for_attestation',
      'ready_for_reattestation',
      'seller_attested',
      'jointly_attested'
    ].includes(canonicalStatusKey)
  ) return 'milestone';
  if (['payout_processing', 'payout_failed', 'paid'].includes(canonicalStatusKey)) return 'payment';
  // An objection is the step before conflict and is counted on its own (ClickUp 869fh4948).
  if (['under_resolution', 'disputed'].includes(canonicalStatusKey)) return 'objection';
  if (canonicalStatusKey === 'external_dispute') return 'dispute';
  return canonicalStatusKey;
}

export function countProjectsByStatus(
  projects: Array<{ status: string; workflowStatus?: string | null }>,
  statusKeys: string[]
): Record<string, number> {
  const counts = Object.fromEntries(statusKeys.map(key => [key, 0]));
  projects.forEach(project => {
    const statusKey = canonicalProjectOverviewStatusKey(project.workflowStatus || project.status);
    if (statusKey in counts) counts[statusKey] += 1;
  });
  return counts;
}

let cachedTemplate: StatusStep[] | null = null;

export async function loadGlobalTemplate(): Promise<StatusStep[]> {
  if (cachedTemplate) return cachedTemplate;
  try {
    const data = await projectStatusApi.getTemplate();
    cachedTemplate = data.baseSteps || [];
    return cachedTemplate!;
  } catch {
    return [];
  }
}

export function clearTemplateCache(): void {
  cachedTemplate = null;
}

export function stepsToOptions(steps: StatusStep[]): StatusOption[] {
  if (!steps || steps.length === 0) {
    return FALLBACK_STATUS.map((s) => ({ value: s.value, label: isEn() ? s.label_en : s.label_sv }));
  }
  return steps.map(s => ({ value: s.key, label: isEn() ? s.label_en : s.label_sv }));
}

const WORKFLOW_STATUS_KEYS = new Set([
  'ready_for_attestation',
  'ready_for_reattestation',
  'seller_attested',
  'jointly_attested',
  'payout_processing',
  'payout_failed',
  'paid',
  'under_resolution',
  'disputed',
  'external_dispute'
]);

/**
 * Filter choices for read-only project views. The configurable project
 * template owns lifecycle statuses, while milestone workflow statuses are
 * derived by the platform and must always remain selectable as well.
 */
export function projectDisplayStatusOptions(steps: StatusStep[]): StatusOption[] {
  const options = stepsToOptions(steps);
  const values = new Set(options.map(option => option.value));
  for (const status of FALLBACK_STATUS) {
    if (!WORKFLOW_STATUS_KEYS.has(status.value) || values.has(status.value)) continue;
    options.push({
      value: status.value,
      label: isEn() ? status.label_en : status.label_sv
    });
  }
  return options;
}

export function getStatusColor(statusKey: string, steps?: StatusStep[]): string {
  const canonicalStatusKey = canonicalProjectStatusKey(statusKey);
  if (steps && steps.length > 0) {
    const step = steps.find(s => s.key === canonicalStatusKey);
    if (step) return step.badgeColor;
  }
  switch (canonicalStatusKey) {
    case 'completed': return 'success';
    case 'in_progress': return 'primary';
    case 'milestone': return 'success';
    case 'ready_for_attestation': return 'info';
    case 'ready_for_reattestation': return 'info';
    case 'seller_attested': return 'primary';
    case 'jointly_attested': return 'success';
    case 'payout_processing': return 'warning';
    case 'payout_failed': return 'danger';
    case 'paid': return 'success';
    case 'under_resolution': return 'warning';
    case 'disputed': return 'warning';
    case 'external_dispute': return 'danger';
    case 'payment': return 'warning';
    case 'contract': return 'info';
    case 'on_hold': return 'warning';
    case 'conflict': return 'danger';
    case 'dispute': return 'danger';
    default: return 'secondary';
  }
}

export function getStatusLabel(statusKey: string, steps?: StatusStep[]): string {
  const canonicalStatusKey = canonicalProjectStatusKey(statusKey);
  if (steps && steps.length > 0) {
    const step = steps.find(s => s.key === canonicalStatusKey);
    if (step) return isEn() ? step.label_en : step.label_sv;
  }
  return fallbackLabel(canonicalStatusKey);
}

export function getSubStatusLabel(statusKey: string, subStatusKey: string | null, steps?: StatusStep[]): string {
  if (!subStatusKey) return '';
  const canonicalStatusKey = canonicalProjectStatusKey(statusKey);
  if (steps && steps.length > 0) {
    const step = steps.find(s => s.key === canonicalStatusKey);
    if (step?.subSteps) {
      const sub = step.subSteps.find(ss => ss.key === subStatusKey);
      if (sub) return isEn() ? sub.label_en : sub.label_sv;
    }
  }
  return subStatusKey;
}
